import { v4 as uuidv4 } from "uuid";

import {
  buildNutritionFoodRecord,
  inflateNutritionFoodRecord,
} from "../../Firestore/nutrition/nutritionRecords";
import {
  validateEquivalentDraftLimits,
} from "../nutritionLimits";
import {
  CATALOG_FOOD_SOURCE_EFORT,
  CATALOG_FOOD_SOURCE_OPEN_SOURCE,
  normalizeCatalogFoodSource,
} from "../foods/supabaseFoodCatalogShared";
import {
  buildIngredientFromFood,
  buildIngredientFromSelectedFood,
  formatQuantityInputValue,
  getFoodStableKey,
  getIngredientCommonMeasures,
  getIngredientQuantityInGrams,
  getIngredientSourceLabel,
  getIngredientSelectedUnitKey,
  getQuantityValueFromGrams,
  getSafeNumber,
  isFallbackIngredient,
} from "../recipes/recipeLogic";
import { buildNutritionEntityDuplicateName } from "../shared/nutritionEntityNameUtils";

const PERSISTABLE_OPTION_SOURCES = new Set([
  CATALOG_FOOD_SOURCE_EFORT,
  CATALOG_FOOD_SOURCE_OPEN_SOURCE,
  "bedca",
  "coach_created",
  "demo",
]);

function normalizeDocId(value, fallback = "") {
  const resolvedValue = String(value ?? fallback).trim();
  return resolvedValue.length > 0 ? resolvedValue.toLowerCase() : String(fallback || "");
}

function cloneOption(option) {
  return {
    ...option,
    origin: option?.origin ? { ...option.origin } : option?.origin,
    commonMeasures: Array.isArray(option?.commonMeasures)
      ? option.commonMeasures.map((measure) => ({ ...measure }))
      : [],
    common_measures: Array.isArray(option?.common_measures)
      ? option.common_measures.map((measure) => ({ ...measure }))
      : [],
    nutrients_per_100g: Array.isArray(option?.nutrients_per_100g)
      ? option.nutrients_per_100g.map((nutrient) => ({ ...nutrient }))
      : [],
    equivalences: [],
  };
}

function getRuntimeOptionSourceLabel(option, inflatedOption, t) {
  return getIngredientSourceLabel(
    {
      ...inflatedOption,
      source:
        inflatedOption?.source ??
        option?.source ??
        (option?.origin?.catalog_food_id
          ? CATALOG_FOOD_SOURCE_OPEN_SOURCE
          : "coach_created"),
      _catalog: Boolean(option?.origin?.catalog_food_id),
    },
    t,
  );
}

function getRuntimeOptionStableKey(option) {
  return String(
    option?.origin?.coach_food_id ??
      option?.origin?.catalog_food_id ??
      option?.id ??
      option?.name ??
      "option",
  );
}

function buildPersistableOptionOrigin(option) {
  if (option?.origin) {
    return option.origin;
  }

  if (getPersistableOptionCatalogSource(option) && option?.catalogFoodId) {
    return {
      catalog_food_id: normalizeDocId(option.catalogFoodId),
    };
  }

  if (option?.source === "coach_created" && (option?.foodId || option?.id)) {
    return {
      coach_food_id: normalizeDocId(option.foodId ?? option.id),
    };
  }

  return null;
}

function getPersistableOptionCatalogSource(option) {
  if (!option?.origin?.catalog_food_id && !option?.catalogFoodId) {
    return null;
  }

  return normalizeCatalogFoodSource(
    option?.catalogSource ?? option?.source,
    {
      defaultSource: CATALOG_FOOD_SOURCE_OPEN_SOURCE,
    },
  );
}

function inflateEquivalentOption(option, t) {
  const inflatedOption = inflateNutritionFoodRecord({
    ...option,
    reference_quantity_g: getSafeNumber(
      option?.reference_quantity_g ?? option?.quantity,
      0,
    ),
  });
  const selectedMeasureId =
    String(option?.selected_measure_id ?? option?.selectedMeasureId ?? "").trim() ||
    null;
  const quantityInGrams = getSafeNumber(
    inflatedOption?.reference_quantity_g ?? option?.reference_quantity_g,
    0,
  );
  const commonMeasures = getIngredientCommonMeasures(inflatedOption);
  const catalogSource = option?.origin?.catalog_food_id
    ? getPersistableOptionCatalogSource(option)
    : null;

  return {
    ...inflatedOption,
    id: normalizeDocId(option?.id, inflatedOption?.id ?? inflatedOption?.name ?? "option"),
    stableKey: getRuntimeOptionStableKey(option),
    foodId: option?.origin?.coach_food_id
      ? normalizeDocId(option.origin.coach_food_id)
      : null,
    catalogFoodId: option?.origin?.catalog_food_id
      ? normalizeDocId(option.origin.catalog_food_id)
      : null,
    quantity: selectedMeasureId
      ? getQuantityValueFromGrams(
          quantityInGrams,
          selectedMeasureId,
          commonMeasures,
        )
      : formatQuantityInputValue(quantityInGrams),
    quantity_g: quantityInGrams,
    quantityUnitKey: selectedMeasureId ?? "grams",
    selected_measure_id: selectedMeasureId,
    source: option?.origin?.catalog_food_id
      ? catalogSource
      : "coach_created",
    databaseKey: option?.origin?.catalog_food_id ? "catalog" : "createdByMe",
    sourceLabel: getRuntimeOptionSourceLabel(option, inflatedOption, t),
    equivalences: [],
  };
}

export function createInitialEquivalentFormState(equivalent, t) {
  return {
    name: String(equivalent?.name || ""),
    options: Array.isArray(equivalent?.options)
      ? equivalent.options.map((option) => inflateEquivalentOption(option, t))
      : [],
  };
}

export function buildEquivalentDuplicateDraft(form = {}, { language = "en" } = {}) {
  return {
    name: buildNutritionEntityDuplicateName("equivalent", form, language),
    options: (Array.isArray(form?.options) ? form.options : []).map(cloneOption),
  };
}

export function buildEquivalentOptionFromFood(food, quantity, language, t) {
  return buildIngredientFromFood(food, quantity, language, t);
}

export function buildEquivalentOptionFromSelectedFood(food, language, t) {
  const option = buildIngredientFromSelectedFood(food, language, t);
  const quantityInGrams = getIngredientQuantityInGrams(option);

  return {
    ...option,
    quantity_g: quantityInGrams,
    quantityUnitKey: getIngredientSelectedUnitKey(option),
    selected_measure_id:
      getIngredientSelectedUnitKey(option) === "grams"
        ? null
        : getIngredientSelectedUnitKey(option),
  };
}

export function addEquivalentOptionToFormOptions(currentOptions, optionToAdd) {
  const resolvedOptions = Array.isArray(currentOptions) ? currentOptions : [];

  return [...resolvedOptions, cloneOption(optionToAdd)];
}

export function sanitizeEquivalentOptionForSave(option) {
  const quantityInGrams = getIngredientQuantityInGrams(option);
  const source = String(option?.source ?? "").trim();

  if (
    quantityInGrams <= 0 ||
    isFallbackIngredient(option) ||
    !PERSISTABLE_OPTION_SOURCES.has(source)
  ) {
    return null;
  }

  const record = buildNutritionFoodRecord({
    ...option,
    quantity: quantityInGrams,
    origin: buildPersistableOptionOrigin(option),
  });
  const selectedMeasureId =
    getIngredientSelectedUnitKey(option) === "grams"
      ? null
      : normalizeDocId(
          option?.selected_measure_id ??
            option?.selectedMeasureId ??
            option?.quantityUnitKey,
        );

  const snapshot = {
    id: normalizeDocId(record.id, option?.stableKey ?? option?.name ?? "option"),
    name: record.name,
    reference_quantity_g: quantityInGrams,
    nutrients_per_100g: record.nutrients_per_100g,
    common_measures: record.common_measures,
    allergen_codes: record.allergen_codes,
    allergen_data_complete: record.allergen_data_complete,
    is_vegetarian: record.is_vegetarian,
    is_vegan: record.is_vegan,
    dietary_data_complete: record.dietary_data_complete,
  };

  if (typeof record?.brand === "string" && record.brand.trim().length > 0) {
    snapshot.brand = record.brand.trim();
  }

  if (typeof record?.store === "string" && record.store.trim().length > 0) {
    snapshot.store = record.store.trim();
  }

  if (record.origin) {
    snapshot.origin = record.origin;
  }

  const catalogSource = getPersistableOptionCatalogSource(option);
  if (catalogSource) {
    snapshot.source = catalogSource;
  }

  if (selectedMeasureId) {
    snapshot.selected_measure_id = selectedMeasureId;
  }

  return snapshot;
}

export function getEquivalentOptionsForSave(options) {
  return (Array.isArray(options) ? options : [])
    .map(sanitizeEquivalentOptionForSave)
    .filter(Boolean);
}

export function getEquivalentDraftValidity(form = {}) {
  const options = Array.isArray(form?.options) ? form.options : [];
  const optionsForSave = getEquivalentOptionsForSave(options);
  const hasName = String(form?.name ?? "").trim().length > 0;
  const hasInvalidOptionQuantities = options.some(
    (option) => getIngredientQuantityInGrams(option) <= 0,
  );
  const hasUnsaveableOptions = optionsForSave.length !== options.length;
  const limitsValidation = validateEquivalentDraftLimits({
    name: form?.name,
    options: optionsForSave,
  });
  const canSave =
    (optionsForSave.length > 0 || hasName) &&
    !hasInvalidOptionQuantities &&
    !hasUnsaveableOptions &&
    limitsValidation.ok;

  return {
    canSave,
    optionsForSave,
    hasInvalidOptionQuantities,
    hasUnsaveableOptions,
    limitsValidation,
  };
}

export function isEquivalentDraftSemanticallyEmpty(form = {}) {
  return (
    String(form?.name ?? "").trim().length === 0 &&
    (Array.isArray(form?.options) ? form.options : []).length === 0
  );
}

export function hasEquivalentUsablePickerItems(equivalentLike = {}) {
  if (getEquivalentOptionsForSave(equivalentLike?.options).length > 0) {
    return true;
  }

  return Array.isArray(equivalentLike?.foods) && equivalentLike.foods.length > 0;
}

export function getEquivalentValidationFeedback(draftSaveState = {}) {
  if (draftSaveState?.hasInvalidOptionQuantities) {
    return {
      inlineMessageKey: "ingredientQuantityRequired-string",
      toast: null,
    };
  }

  if (draftSaveState?.hasUnsaveableOptions) {
    return {
      inlineMessageKey: "equivalentOptionUnsaveableDescription-string",
      toast: {
        titleKey: "equivalentOptionUnsaveableTitle-string",
        descriptionKey: "equivalentOptionUnsaveableDescription-string",
      },
    };
  }

  return null;
}

export function shouldShowEquivalentUnsaveableOptionsToastTransition({
  previousHasUnsaveableOptions = false,
  nextHasUnsaveableOptions = false,
} = {}) {
  return !previousHasUnsaveableOptions && nextHasUnsaveableOptions;
}

export function buildEquivalentRecordForSave(form = {}, equivalent = {}) {
  const { canSave, optionsForSave } = getEquivalentDraftValidity(form);
  if (!canSave) {
    return null;
  }

  return {
    ...equivalent,
    id: equivalent?.id || uuidv4(),
    name: String(form?.name ?? "").trim(),
    options: optionsForSave,
    _rev: equivalent?._rev,
  };
}
